import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteRecordNormalized,
  type RouteRecordRaw,
} from 'vue-router'
import { getAppParams } from '@/stores/app'
import { failRouteLoading, finishRouteLoading, startRouteLoading } from '@/composables/useRouteLoading'

import { loginRoutes } from './modules/login'
import { tabHomeRoutes } from './modules/tabHome'
import { homeRoutes } from './modules/home'
import { contentRoutes } from './modules/content'
import { activityRoutes } from './modules/activity'
import { profileRoutes } from './modules/profile'
import { meRoutes } from './modules/me'
import { webRoutes } from './modules/web'
import { ersRoutes } from './modules/ers'
import { bindDeviceRoutes } from './modules/bindDevice'

const routes: RouteRecordRaw[] = [
  ...loginRoutes,
  ...tabHomeRoutes,
  ...homeRoutes,
  ...contentRoutes,
  ...activityRoutes,
  ...profileRoutes,
  ...meRoutes,
  ...webRoutes,
  ...ersRoutes,
  ...bindDeviceRoutes,
  {
    // 未匹配路由 → 404 页面
    path: '/:pathMatch(.*)*',
    name: 'notFound',
    component: () => import('@/pages/NotFound/index.vue'),
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

// 使用路由记录对象作为页面标识：动态参数变化仍属于同一个页面组件。
// 集合仅存活于当前 WebView，页面成功进入后才记录；加载失败时下次仍会展示进度。
const loadedPageRecords = new Set<RouteRecordNormalized>()
let loadingTarget: RouteLocationNormalized | undefined

function getPageRecords(route: RouteLocationNormalized): RouteRecordNormalized[] {
  return route.matched.filter((record) => record.components && Object.keys(record.components).length > 0)
}

function startLoadingForUnvisitedPage(route: RouteLocationNormalized): void {
  const hasUnvisitedPage = getPageRecords(route).some((record) => !loadedPageRecords.has(record))
  if (!hasUnvisitedPage) return

  loadingTarget = route
  startRouteLoading()
}

function markPageRecordsLoaded(route: RouteLocationNormalized): void {
  getPageRecords(route).forEach((record) => loadedPageRecords.add(record))
}

// 全局鉴权守卫：除公开路由外，未登录一律跳登录页。
// 公开 = 登录流 + web 目录（独立 H5：download/invite/Ers 子系统）+ 根 + 404；
// 其余（首页/内容/个人/我的/好友/绑机等）需登录。
const PUBLIC_PREFIXES = ['/login', '/bind-phone', '/download', '/invite', '/ers']
function isPublicRoute(route: RouteLocationNormalized): boolean {
  return route.name === 'notFound'
    || route.path === '/'
    || PUBLIC_PREFIXES.some((prefix) => route.path === prefix || route.path.startsWith(`${prefix}/`))
}

router.beforeEach((to) => {
  if (!isPublicRoute(to) && !getAppParams().token) return { path: '/login' }
  startLoadingForUnvisitedPage(to)
  return true
})

router.afterEach((to, _from, failure) => {
  if (!failure) markPageRecordsLoaded(to)
  if (loadingTarget !== to) return

  loadingTarget = undefined
  if (failure) failRouteLoading()
  else finishRouteLoading()
})

router.onError((_error, to) => {
  if (loadingTarget !== to) return
  loadingTarget = undefined
  failRouteLoading()
})
